/**
 * T281 — Notifications API hooks.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { notificationKeys } from './keys';
import { useActiveTenantId } from '../../hooks/useTenantContext';

export interface NotificationDto {
  id: string;
  type: string;
  payload: Record<string, unknown>;
  linkUrl: string | null;
  readAt: string | null;
  createdAt: string;
}

export function useNotifications(onlyUnread = false) {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: notificationKeys.list(tenantId, onlyUnread),
    queryFn: () =>
      api.get<{ items: NotificationDto[] }>(
        `/notifications${onlyUnread ? '?unread=true' : ''}`
      ),
    enabled: tenantId !== null,
    refetchInterval: 10_000, // 10s polling
  });
}

export function useMarkNotificationRead() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (id: string) => api.post(`/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all(tenantId) }),
  });
}

export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: () => api.post('/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: notificationKeys.all(tenantId) }),
  });
}
